"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.syncRefinements = exports.withRefinementsSyncStatus = exports.getOrCreateRefinementGroupByName = void 0;
const api_1 = require("../../_generated/client/api");
const helpers_1 = require("../helpers");
/**
 * Function gets refiments. If refiment group does not exsist it will be created
 * @param access - Access configuration
 * @param internalGroupId - Internal group Id
 * @param refinementGroupName - Refinements Group Name
 */
async function getOrCreateRefinementGroupByName(access, internalGroupId, refinementGroupName) {
    const refimentsApi = new api_1.RefinementsApi(await access());
    const response = await refimentsApi.getRefinementsGroups(internalGroupId);
    const createRefinementsGroupResource = async () => {
        return refimentsApi
            .createRefinementsGroup(internalGroupId, {
            name: refinementGroupName,
        })
            .then((q) => q.data);
    };
    if (response.status === 404) {
        return createRefinementsGroupResource();
    }
    const refGroup = response.data.find((q) => q.resource.name === refinementGroupName);
    if (!refGroup)
        return createRefinementsGroupResource();
    return refGroup;
}
exports.getOrCreateRefinementGroupByName = getOrCreateRefinementGroupByName;
/**
 * Function will update sync start abd fisnish status in dashboard.
 * @example
 * ```typescript
 * const access = getDashboardAccess() // DashboardAccess
 * const refimentsConfig = { internalGroupName: "hubo-be", refinementGroupName: "Hubo.fr" }
 *
 * withRefinementsSyncStatus(access, refimentsConfig, async () => {
 *      // Refinements retrieval function
 *      const refiments = await getRefinementsFromAnalytics()
 *
 *      // Execute actual refiment sync
 *      return syncRefinements(access, refimentsConfig, refiments)
 * })
 *
 * ```
 * @param access - Access function
 */
async function withRefinementsSyncStatus(access, refimentsConfig, fn) {
    const { internalGroupName, refinementGroupName } = refimentsConfig;
    const groupId = await helpers_1.getOrCreateGroupByName(access, internalGroupName);
    const refimentGroupModel = await getOrCreateRefinementGroupByName(access, groupId.id, refinementGroupName);
    await new api_1.RefinementsApi(await access()).updateRefinementsGroupLastSyncStart(groupId.id, refimentGroupModel.id);
    return fn().finally(async () => {
        await new api_1.RefinementsApi(await access()).updateRefinementsGroupLastSyncEnd(groupId.id, refimentGroupModel.id);
    });
}
exports.withRefinementsSyncStatus = withRefinementsSyncStatus;
/**
 * Function will post process refiments and upload them to client dashboard
 * @param configuration - Dashboard access function
 * @param data - Any iterable with refiments
 * @param options - Optional configuration
 */
async function syncRefinements(configuration, refimentsConfig, data, options) {
    const { internalGroupName, refinementGroupName } = refimentsConfig;
    const { maxPendingRefinements = 100 } = options || {};
    // Variable for cacheing refiments
    const groupModel = await helpers_1.getOrCreateGroupByName(configuration, internalGroupName);
    const refimentModel = await getOrCreateRefinementGroupByName(configuration, groupModel.id, refinementGroupName);
    const result = await new api_1.RefinementsApi(await configuration()).getRefinements(groupModel.id, refimentModel.id);
    const pendingList = result.data.filter((q) => q.resource.status === api_1.RefinementResourceModelStatusEnum.Pending);
    const hiddenList = result.data.filter((q) => q.resource.status === api_1.RefinementResourceModelStatusEnum.Hidden);
    for await (const pending of pendingList) {
        await new api_1.RefinementsApi(await configuration()).deleteRefinement(groupModel.id, refimentModel.id, pending.id);
    }
    const uniquePair = (r) => `${r.fromQuery.id}:${r.toQuery.id}`;
    const hiddenListSet = new Set(hiddenList.map((q) => uniquePair(q.resource)));
    const createRefinement = async (item) => {
        return new api_1.RefinementsApi(await configuration())
            .createRefinement(groupModel.id, refimentModel.id, item)
            .catch((q) => {
            return new Error(q.toString());
        });
    };
    const stats = {
        refinementCount: 0
    };
    for await (const refiment of data) {
        if (stats.refinementCount === maxPendingRefinements) {
            return;
        }
        if (hiddenListSet.has(uniquePair(refiment)))
            continue;
        const err = await createRefinement(refiment);
        if (err instanceof Error) {
            continue;
        }
        // tslint:disable-next-line: no-object-mutation
        stats.refinementCount += 1;
    }
}
exports.syncRefinements = syncRefinements;
